import React, { useEffect, useState, useMemo } from 'react';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { FormProvider, useForm } from 'react-hook-form';
import { joiResolver } from '@hookform/resolvers/joi';

import { DSelect, HDatePicker, HInput, HTextarea, HCheckbox, HRadio } from '../../../../validation/HForm';
import {CREDIT_TYPE, DEBIT_TYPE, paymentOperationOptions, sections, sectionsVisem} from '../../../../helpers/data-constants';
import LoaderCommon from '../Loader/LoaderCommon';
import { calculationAccess } from '../../../../helpers/common';
import { listRoles } from '../../../../helpers/data-constants';
import {createTransactionBatchScheme} from '../../../../validation/scheme/transactionScheme';
import { Message, useRecalculation } from '../../../utilites';
import { getEmployee, resetHandbook } from '../../../../redux/actions/handbookAction';
import { getLastTransactionBatch } from '../../../../redux/actions/transactionsAction';

import CrossIcon from '../../../../assets/icons/cross.svg';

const filterByType = (type, feature) => {
  let sectionArr = Object.values(feature ? sections : sectionsVisem);
  return sectionArr.filter((value) => {
    return value.type === type;
  });
};

const getPrompt = (type, feature) => {
  return filterByType(type, feature).map(({ code, prompt }) => {
    return { value: code, prompt };
  });
};

const getNonCashOperation = () => Object.values(sections).find((value) => value.code === 1013);

const getSections = (type, feature, ticketLost, isRecalculationOrderAfterClosing) => {
  return filterByType(type, feature).map(({ code, name, isDisabled, isDefaultPayment }) => {
    if (ticketLost && (code === 1006 || code === 1007)) {
      if (code === 1006) {
        return { value: sectionsVisem.SALE.code, label: sectionsVisem.SALE.name, isDisabled: false };
      } else if (code === 1007) {
        return { value: sectionsVisem.TICKET_LOST.code, label: sectionsVisem.TICKET_LOST.name, isDisabled: false };
      } 
    } else if (isRecalculationOrderAfterClosing && code === 1008) {
      return { value: code, label: name, isDisabled: true };
    } else {
      return { value: code, label: name, isDisabled, isDefaultPayment };
    }
    return { value: code, label: name, isDisabled };
  });
};

const rolesForAccessByDate = [
  listRoles.ADMIN,
  listRoles.DIRECTOR,
  listRoles.ACCOUNTANT,
  listRoles.BRANCH_DIRECTOR,
  listRoles.GOODS_SPECIALIST
];

const ModalCommonOrderContent = ({
  cashBoxName,
  cashBoxId,
  type,
  explanation,
  onTransactionBatchSubmit,
  closeModalTransactionBatch,
  disabledButton
}) => {
  const { users, employeesError, usersLoading } = useSelector(({ handbook }) => handbook);
  const { lastTransactionBatch, lastTransactionBatchLoading, commonError } = useSelector(
    ({ transactions }) => transactions,
  );

  const [transactionBatchDoc, setTransactionBatchDoc] = useState(1);
  const [employees, setEmployees] = useState([{ value: 0, label: null }]);
  const [disabled, setDisabled] = useState(false);
  const [isDisabledPayment, setIsDisabledPayment] = useState(false)
  const {accessFeatures} = useSelector(({user}) => user);

  const dispatch = useDispatch();

  const accessByDate = calculationAccess(rolesForAccessByDate);
  const composeError = employeesError || commonError || explanation;
  const { isRecalculationOrderAfterClosing } = useRecalculation({accessFeatures});
  const sections = useMemo(() => getSections(type === 'CREDIT' ? CREDIT_TYPE : DEBIT_TYPE, accessFeatures["cashboxNameTypeOne"], accessFeatures["ticketLost"], isRecalculationOrderAfterClosing), [type]);
  const prompt = useMemo(() => getPrompt(type === 'CREDIT' ? CREDIT_TYPE : DEBIT_TYPE, accessFeatures["cashboxNameTypeOne"]), [type]);
  const isNonCashOperationCrediton = accessFeatures['non_cash.operations.type']

  const setSections = () => {
    let sectionsOperation = sections
    let promtOperation = prompt
    if(isNonCashOperationCrediton && type === 'CREDIT') {
      const nonCashOperation = getNonCashOperation()
      sectionsOperation = [
        ...sections, 
        { value: nonCashOperation.code, label: nonCashOperation.name, isDisabled: false, isDefaultPayment: true }]
      promtOperation = [...prompt, { value: nonCashOperation.code, prompt: nonCashOperation.prompt }]

      return {sectionsOperation, promtOperation}
    }

    return {sectionsOperation, promtOperation}
  }

  const form = useForm({
    mode: 'onChange',
    resolver: joiResolver(createTransactionBatchScheme(type)),
  });

  useEffect(() => {
    if (type) {
      dispatch(getLastTransactionBatch({type, cashBoxId}));
    }
  }, [type, dispatch]);

  useEffect(() => {
    if (users.length) {
      setEmployees((prev) => [prev[0], ...users.map((u) => ({ value: u.id, label: u.fullName }))]);
    } else {
      setEmployees((prev) => [prev[0]]);
    }
  }, [users]);

  useEffect(() => {
    if (lastTransactionBatch && lastTransactionBatch?.documentNumber) {
      setTransactionBatchDoc(parseInt(lastTransactionBatch.documentNumber) + 1);
    }
  }, [lastTransactionBatch]);

  useEffect(() => {
    return () => {
      dispatch(resetHandbook());
    };
  }, [dispatch]);

  let nextDocumentNumber =
    lastTransactionBatch && lastTransactionBatch.documentNumber
      ? parseInt(lastTransactionBatch.documentNumber) + 1
      : 1;

  const returnElementDateFromRole = () => {
    if (accessByDate) {
      return <HDatePicker
        h="true"
        className="input"
        defaultValue={moment().utc().format('YYYY-MM-DD')}
        name="date"
        id="date" />;
    }
    return (
      <HDatePicker
        h="true"
        className="input"
        name="date"
        id="date"
        defaultValue={moment().utc().format('YYYY-MM-DD')}
        min={moment().utc().format('YYYY-MM-DD')}
        max={moment().utc().format('YYYY-MM-DD')}
      />
    );
  };

  const handleGetEmployee = (e, action) => {
    if (action.action === 'input-change' && e.length >= 2) {
      dispatch(getEmployee({ bySearch: e, document: true , active: 'active=true'}));
    }

    setEmployees((prev) => [{ value: 0, label: e }, ...prev.slice(1)]);
  };

  const handleChoose = (i) => {
    if (type !== 'CREDIT') {
      const passport = users.find((u) => u.id === i.value)?.document;
      form.setValue('passport', passport || '');
    }
  };

  const handleChange = () => {
    const value = form.getValues();
    if (value.toPerson) {
      if (type === 'CREDIT') {
        form.setValue(
          'accepted',
          { value: 0, label: 'от физических лиц' },
          { shouldValidate: true },
        );
        setDisabled(true);
      } else {
        form.setValue(
          'accepted',
          { value: 0, label: 'физическим лицам' },
          { shouldValidate: true },
        );
        setDisabled(true);
      }
    } else {
      form.setValue('accepted', [{ value: 0, label: null }]);
      setDisabled(false);
    }
  };

  const documentNumberChange = (e) => {
    setTransactionBatchDoc(e.target.value);
  };

  const setTextarea = (e) => {
    const text = setSections()
      .promtOperation.find(({ value }) => value === e.value)
      .prompt.replace(/\s*,\s*/g, "\n")
      .trim();
    const selectedSection = setSections().sectionsOperation.find(({ value }) => value === e.value);

    const defaultPaymentValue = selectedSection.isDefaultPayment 
      ? paymentOperationOptions[1].label 
      : paymentOperationOptions[0].label

    setIsDisabledPayment(selectedSection.isDefaultPayment)
    
    form.setValue('description', text, { shouldValidate: true });
    form.setValue('cashType', defaultPaymentValue);
  };

  const styleOperationData = type === 'CREDIT' ? 'modal-incoming-transaction-order' : 'modal-expenditure-transaction'

  return (
    <>
      {composeError && <Message type="error" text={composeError} />}
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(onTransactionBatchSubmit)}>
          <div className="modal-header">
            <h2>
              Сводный ордер по {type === 'CREDIT' ? 'приходным' : 'расходным'} операциям №
              {transactionBatchDoc ? transactionBatchDoc : 1}
            </h2>
            <button
              type="button"
              className="button-icontrigger modal-closetrigger"
              onClick={closeModalTransactionBatch}>
              <span className="icon">
                <CrossIcon />
              </span>
            </button>
          </div>
          <div className="modal-body">
            {lastTransactionBatchLoading ? (
              <LoaderCommon />
            ) : (
              <div>
                <fieldset>
                  <div className="modal-subdivision">
                    <span className="label">{cashBoxName}</span>
                  </div>
                  <div className="d-flex align-baseline flex-space-between">
                    {lastTransactionBatch?.documentNumber && (
                      <span className={styleOperationData}>
                        <big>
                          {`Предыдущий №${lastTransactionBatch.documentNumber} от
                          ${moment(lastTransactionBatch.createDate).utc().format('DD.MM.YYYY')}${
                          lastTransactionBatch.user ? ', ' + lastTransactionBatch.user.fullName : ''}`}
                        </big>
                      </span>
                    )}
                  </div>
                </fieldset>
                <fieldset>
                  <div className="form-row">
                    <div className="form-row-item">
                      <div className="form-element">
                        <label className="form-element-label" htmlFor="section">
                          Основание
                        </label>
                        <div className="form-element-data combobox-lg">
                          <DSelect
                            h="true"
                            name="section"
                            inputId="section"
                            defaultValue={sections[0]}
                            options={setSections().sectionsOperation}
                            isSearchable={true}
                            isLoading={false}
                            placeholder="Основание"
                            handleOnChange={setTextarea}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="form-row-item">
                      <div className="form-element">
                        <label className="form-element-label" htmlFor="date">
                          Дата
                        </label>
                        <div className="form-element-data date">{returnElementDateFromRole()}</div>
                      </div>
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-row-item">
                      <div className="form-element">
                        <label className="form-element-label" htmlFor="documentNumber">
                          Номер документа
                        </label>
                        <div className="form-element-data">
                          <HInput
                            h="true"
                            className="input"
                            type="number"
                            name="documentNumber"
                            id="documentNumber"
                            onChange={documentNumberChange}
                            defaultValue={nextDocumentNumber}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="form-row-item">
                      <div className="form-element">
                        <label className="form-element-label" htmlFor="toPerson">
                          {type === 'CREDIT' ? 'От физических лиц' : 'Физическим лицам'}
                        </label>
                        <div className="form-element-data">
                          <HCheckbox
                            h="true"
                            name="toPerson"
                            className="input"
                            id="toPerson"
                            onChange={handleChange}
                          />
                        </div>
                      </div>
                    </div>
                    {isNonCashOperationCrediton && <div className="form-row-item">
                      <div className="form-element">
                        <label className="form-element-label" htmlFor="#">
                          Форма расчёта
                        </label>
                        <HRadio
                          h="true"
                          labels={paymentOperationOptions.map((option) => (option.label))}
                          name="cashType"
                          checkboxStyle={true}
                          defaultCheckedLabel={'Наличный'}
                          disabled={isDisabledPayment}
                        />
                      </div>
                    </div>}
                  </div>
                  <div className="form-row">
                    <div className="form-row-item">
                      <div className="form-element">
                        <label className="form-element-label" htmlFor="accepted">
                          {type === 'CREDIT' ? 'Принято от' : 'Выдать'}
                        </label>
                        <div className="form-element-data combobox-lg">
                          <DSelect
                            h="true"
                            name="accepted"
                            inputId="accepted"
                            options={employees}
                            handleOptions={handleGetEmployee}
                            handleOnChange={handleChoose}
                            isLoading={usersLoading}
                            placeholder="Введите фамилию"
                            isDisabled={disabled}
                            isClearable={disabled}
                          />
                        </div>
                      </div>
                    </div>
                    {type === 'DEBIT' && (
                      <div className="form-row-item">
                        <div className="form-element">
                          <label className="form-element-label" htmlFor="passport">
                            Паспортные данные
                          </label>
                          <div className="form-element-data">
                            <HInput
                              h="true"
                              className="input"
                              type="text"
                              name="passport"
                              id="passport"
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="form-element">
                    <label className="form-element-label" htmlFor="description">
                      Приложение
                    </label>
                    <div className="form-element-data textarea">
                      <HTextarea
                        h="true"
                        className="input"
                        rows="4"
                        cols="10"
                        name="description"
                        id="description"
                        defaultValue={prompt[0].prompt}
                      />
                    </div>
                  </div>
                </fieldset>
              </div>
            )}
          </div>
          <div className="modal-footer">
            <button className="button" disabled={disabledButton}>Сохранить</button>
            <button type="button" className="button-light" onClick={closeModalTransactionBatch}>
              Отменить
            </button>
          </div>
        </form>
      </FormProvider>
    </>
  );
};

export default ModalCommonOrderContent;
